import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPayouts, updatePayoutStatus } from "../../../slice/payout/payoutSlice";

const PayoutReport = () => {
  const dispatch = useDispatch();

  const payoutState = useSelector((state) => state.payout) || {};
  const {
    payouts = [],
    loading = false,
    error = null,
    updatingId = null,
  } = payoutState;

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [confirmPayout, setConfirmPayout] = useState(null);

  useEffect(() => {
    dispatch(fetchPayouts({ page: 1, limit: 100 }));
  }, [dispatch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const formatAmount = (n) =>
    typeof n === "number"
      ? `$${n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "-";

  const filteredPayouts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromDate = dateFrom ? new Date(dateFrom + "T00:00:00") : null;
    const toDate = dateTo ? new Date(dateTo + "T23:59:59.999") : null;

    return payouts.filter((p) => {
      const matchesStatus =
        statusFilter === "all" ? true : p.status === statusFilter;
      if (!matchesStatus) return false;

      if (q) {
        const payoutId = (p.payoutId || "").toLowerCase();
        const vendorName = (p.vendor?.vendorName || "").toLowerCase();
        if (!payoutId.includes(q) && !vendorName.includes(q)) return false;
      }

      if (fromDate || toDate) {
        const requested = p.requestedDate ? new Date(p.requestedDate) : null;
        if (!requested || isNaN(requested.getTime())) return false;
        if (fromDate && requested < fromDate) return false;
        if (toDate && requested > toDate) return false;
      }

      return true;
    });
  }, [payouts, search, statusFilter, dateFrom, dateTo]);

  const clearDateFilter = () => {
    setDateFrom("");
    setDateTo("");
  };

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  const hasActiveFilters =
    search.trim() !== "" || statusFilter !== "all" || dateFrom || dateTo;

  const requestMarkPaid = (payout) => setConfirmPayout(payout);
  const closeConfirm = () => setConfirmPayout(null);
  const handleConfirmMarkPaid = () => {
    if (!confirmPayout) return;
    dispatch(updatePayoutStatus({ id: confirmPayout._id, status: "paid" }));
    setConfirmPayout(null);
  };

  const statusOptions = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "paid", label: "Paid" },
  ];

  return (
    <div
      style={{
        background: "#f6f7fb",
        minHeight: "100vh",
        padding: "28px 32px",
      }}
    >
      <div style={{ marginBottom: 22 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 30,
            fontWeight: 800,
            color: "#111827",
          }}
        >
          Payouts
        </h1>
        <p style={{ margin: "6px 0 0", color: "#9ca3af", fontSize: 14 }}>
          {filteredPayouts.length} of {payouts.length} payouts
        </p>
      </div>

      {/* ── FILTER TOOLBAR ── */}
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #eef0f4",
          padding: "18px 20px",
          marginBottom: 20,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 14,
        }}
      >
        {/* Search */}
        <div style={{ position: "relative", flex: "1 1 240px", minWidth: 220 }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9ca3af"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Search payout ID or vendor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 14px 10px 38px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              fontSize: 14,
              outline: "none",
              background: "#f9fafb",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Divider */}
        <div style={{ width: 1, height: 28, background: "#eef0f4" }} />

        {/* Status pills */}
        <div
          style={{
            display: "flex",
            gap: 4,
            background: "#f3f4f6",
            padding: 4,
            borderRadius: 10,
          }}
        >
          {statusOptions.map((opt) => {
            const active = statusFilter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatusFilter(opt.value)}
                style={{
                  padding: "7px 16px",
                  borderRadius: 8,
                  border: "none",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: active ? "#111827" : "transparent",
                  color: active ? "#fff" : "#6b7280",
                  transition: "all 0.15s ease",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Divider */}
        <div style={{ width: 1, height: 28, background: "#eef0f4" }} />

        {/* Date range */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "6px 10px",
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9ca3af"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>

          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 13,
              color: dateFrom ? "#111827" : "#9ca3af",
              outline: "none",
              padding: "4px 2px",
              fontWeight: 600,
            }}
          />

          <span style={{ color: "#d1d5db", fontSize: 13 }}>→</span>

          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 13,
              color: dateTo ? "#111827" : "#9ca3af",
              outline: "none",
              padding: "4px 2px",
              fontWeight: 600,
            }}
          />

          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={clearDateFilter}
              title="Clear dates"
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: "none",
                background: "#e5e7eb",
                color: "#4b5563",
                fontSize: 13,
                lineHeight: "20px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: 2,
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearAllFilters}
            style={{
              marginLeft: "auto",
              padding: "9px 16px",
              borderRadius: 10,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#dc2626",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Clear all
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {(dateFrom || dateTo) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 20,
              background: "#eef2ff",
              color: "#4338ca",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            📅 {dateFrom ? formatDate(dateFrom) : "Any"} → {dateTo ? formatDate(dateTo) : "Any"}
          </span>
        </div>
      )}

      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #eef0f4",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
            Loading...
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626" }}>
            {typeof error === "string" ? error : "Something went wrong"}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafbfc" }}>
                  {[
                    "Si No",
                    "PAYOUT ID",
                    "VENDOR",
                    "AMOUNT",
                    "STATUS",
                    "REQUESTED",
                    "SETTLED",
                    "ACTION",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "14px 20px",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        color: "#9ca3af",
                        borderBottom: "1px solid #eef0f4",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPayouts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        textAlign: "center",
                        padding: "50px 0",
                        color: "#aaa",
                      }}
                    >
                      No payout records found
                    </td>
                  </tr>
                ) : (
                  filteredPayouts.map((p, index) => (
                    <tr key={p._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 13, fontWeight: 600, color: "#111827" }}>
                        {p.payoutId}
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 14, color: "#374151" }}>
                        {p.vendor?.vendorName || "-"}
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 14, fontWeight: 600, color: "#111827" }}>
                        {formatAmount(p.amount)}
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <span
                          style={{
                            padding: "5px 12px",
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            textTransform: "uppercase",
                            background: p.status === "paid" ? "#ecfdf5" : "#fffbeb",
                            color: p.status === "paid" ? "#059669" : "#b45309",
                          }}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {formatDate(p.requestedDate)}
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {formatDate(p.settledDate)}
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        {p.status === "pending" ? (
                          <button
                            onClick={() => requestMarkPaid(p)}
                            disabled={updatingId === p._id}
                            style={{
                              padding: "7px 14px",
                              borderRadius: 8,
                              border: "none",
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#fff",
                              background: updatingId === p._id ? "#a7f3d0" : "#059669",
                              cursor: updatingId === p._id ? "not-allowed" : "pointer",
                            }}
                          >
                            {updatingId === p._id ? "Updating..." : "Mark as Paid"}
                          </button>
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── CONFIRM MARK AS PAID MODAL ── */}
      {confirmPayout && (
        <>
          <div
            onClick={closeConfirm}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 1040,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1050,
              width: "100%",
              maxWidth: 440,
              background: "#fff",
              borderRadius: 20,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              padding: "32px 32px 28px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 24,
              }}
            >
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Mark as Paid</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{
                    background: "#ecfdf5",
                    color: "#059669",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1,
                    padding: "4px 12px",
                    borderRadius: 20,
                    border: "1px solid #059669",
                  }}
                >
                  CONFIRM
                </span>
                <button
                  onClick={closeConfirm}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    cursor: "pointer",
                    fontSize: 16,
                    color: "#555",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <hr style={{ borderColor: "#f0f0f0", marginBottom: 20 }} />

            <p style={{ margin: "0 0 24px", fontSize: 14, color: "#4b5563", lineHeight: 1.6 }}>
              Are you sure you want to mark payout{" "}
              <strong style={{ color: "#111827" }}>{confirmPayout.payoutId}</strong> for{" "}
              <strong style={{ color: "#111827" }}>
                {confirmPayout.vendor?.vendorName || "this vendor"}
              </strong>{" "}
              (<strong style={{ color: "#111827" }}>{formatAmount(confirmPayout.amount)}</strong>) as{" "}
              <strong style={{ color: "#059669" }}>Paid</strong>? This action cannot be undone.
            </p>

            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={closeConfirm}
                style={{
                  padding: "11px 24px",
                  borderRadius: 10,
                  border: "1.5px solid #e5e7eb",
                  background: "#f9fafb",
                  color: "#555",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                No, Cancel
              </button>
              <button
                onClick={handleConfirmMarkPaid}
                disabled={updatingId === confirmPayout._id}
                style={{
                  flex: 1,
                  padding: "11px 24px",
                  borderRadius: 10,
                  border: "none",
                  background: updatingId === confirmPayout._id ? "#a7f3d0" : "#059669",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: updatingId === confirmPayout._id ? "not-allowed" : "pointer",
                }}
              >
                {updatingId === confirmPayout._id ? "Updating..." : "Yes, Mark as Paid"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PayoutReport;